/**
 * Agent detail page - FE-22.
 * Info card, governance actions, timeline, reports, and H2A stats.
 */

import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  type ActivateAgentPaidAccessPayload,
  activateAgentPaidAccess,
} from "../../api/admin-agent-commerce";
import {
  type RotateAgentIdentityPayload,
  rotateAgentIdentity,
} from "../../api/admin-agent-identity-rotation";
import {
  type AgentDetail,
  banAgent,
  fetchAgentDetail,
  restoreAgent,
  restrictAgent,
  unpublishAgentListing,
} from "../../api/admin-agents";
import { ApiError } from "../../api/client";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { shortId } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { getLocale, t } from "../../i18n";
import { resolveAdminErrorMessage } from "../../lib/admin-error-messages";
import { type AgentAction, AgentActionButtons } from "./AgentDetailActions";
import {
  type AgentDetailTab,
  AgentDetailTabBar,
  AgentDetailTabContent,
  H2aStatsBar,
  InfoCard,
} from "./AgentDetailContent";
import { AgentIdentityRotationDialog } from "./AgentIdentityRotationDialog";
import { AgentPaidAccessActivationDialog } from "./AgentPaidAccessActivationDialog";
import { s } from "./agent-detail-styles";

type SimpleAgentAction = Exclude<
  AgentAction,
  "rotate" | "activate-paid-access"
>;

export function AgentDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [agent, setAgent] = useState<AgentDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<AgentDetailTab>("Overview");
  const [dialog, setDialog] = useState<AgentAction | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      setAgent(await fetchAgentDetail(id));
    } catch (e) {
      setError(
        e instanceof Error ? e.message : t("agent_detail.failed_to_load"),
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleConfirm = useCallback(
    async (reason: string): Promise<void> => {
      if (!id || !dialog || !isSimpleAction(dialog)) return;
      try {
        await runSimpleAction(dialog, id, reason);
        setDialog(null);
        void load();
      } catch (e) {
        handleActionError(e, setError);
        setDialog(null);
      }
    },
    [id, dialog, load],
  );

  const handleRotate = useCallback(
    async (payload: RotateAgentIdentityPayload): Promise<void> => {
      if (!agent) return;
      try {
        await rotateAgentIdentity(agent.id, payload);
        setDialog(null);
        void load();
      } catch (e) {
        handleActionError(e, setError);
        setDialog(null);
      }
    },
    [agent, load],
  );

  const handleActivatePaidAccess = useCallback(
    async (payload: ActivateAgentPaidAccessPayload): Promise<void> => {
      if (!agent) return;
      try {
        await activateAgentPaidAccess(agent.id, payload);
        setDialog(null);
        void load();
      } catch (e) {
        handleActionError(e, setError);
        setDialog(null);
      }
    },
    [agent, load],
  );

  if (loading) return <p style={s.msg}>{t("agent_detail.loading")}</p>;
  if (error)
    return <p style={{ ...s.msg, color: "var(--color-danger)" }}>{error}</p>;
  if (!agent) return <p style={s.msg}>{t("agent_detail.not_found")}</p>;

  const simpleAction =
    dialog !== null && isSimpleAction(dialog) ? dialog : null;

  return (
    <div>
      <PageHeader
        title={agent.name}
        description={t("agent_detail.created_by", { name: agent.creator_name })}
        actions={<AgentActionButtons agent={agent} onAction={setDialog} />}
      />
      <InfoCard agent={agent} />
      <H2aStatsBar stats={agent.h2a_stats} />
      <AgentDetailTabBar active={tab} onChange={setTab} />
      <AgentDetailTabContent tab={tab} agent={agent} />
      <AgentIdentityRotationDialog
        open={dialog === "rotate"}
        agentName={agent.name}
        onConfirm={(payload) => void handleRotate(payload)}
        onCancel={() => setDialog(null)}
      />
      <AgentPaidAccessActivationDialog
        open={dialog === "activate-paid-access"}
        agentName={agent.name}
        onConfirm={(payload) => void handleActivatePaidAccess(payload)}
        onCancel={() => setDialog(null)}
      />
      <ConfirmDialog
        open={simpleAction !== null}
        title={simpleActionTitle(simpleAction)}
        description={simpleActionDescription(simpleAction, agent)}
        reasonRequired
        reasonMinLength={10}
        onConfirm={(reason) => void handleConfirm(reason)}
        onCancel={() => setDialog(null)}
      />
    </div>
  );
}

function isSimpleAction(action: AgentAction): action is SimpleAgentAction {
  return action !== "rotate" && action !== "activate-paid-access";
}

async function runSimpleAction(
  action: SimpleAgentAction,
  agentId: string,
  reason: string,
): Promise<void> {
  if (action === "restrict") await restrictAgent(agentId, reason);
  else if (action === "restore") await restoreAgent(agentId, reason);
  else if (action === "ban") await banAgent(agentId, reason);
  else await unpublishAgentListing(agentId, reason);
}

function simpleActionTitle(action: SimpleAgentAction | null): string {
  if (action === "restrict") return t("agent_detail.restrict_title");
  if (action === "restore") return t("agent_detail.restore_title");
  if (action === "ban") return t("agent_detail.ban_title");
  return t("agent_detail.unpublish_title");
}

function simpleActionDescription(
  action: SimpleAgentAction | null,
  agent: AgentDetail,
): string {
  if (action === "restrict") return t("agent_detail.restrict_desc");
  if (action === "restore") return t("agent_detail.restore_desc");
  if (action === "ban") {
    return t("agent_detail.ban_desc", {
      name: agent.name,
      id: shortId(agent.id),
    });
  }
  return t("agent_detail.unpublish_desc");
}

function handleActionError(
  error: unknown,
  setError: (message: string) => void,
): void {
  if (error instanceof ApiError) {
    setError(resolveAdminErrorMessage(error.errorCode, getLocale()));
  } else {
    setError(
      error instanceof Error ? error.message : t("common.action_failed"),
    );
  }
}
